/**
 * `NoAdsPopup` — the Remove Ads store panel.
 *
 * ⚠️ **Nothing in this file is read from the decomp, and there is no prefab for
 * it.** The shipped game has no IAP at all: `Resources/popups/` holds
 * `FailPresenter`, `SuccessPresenter` and `SettingsPresenter` and no store
 * panel, and the only monetization class in `dump.cs` is `AdService`
 * (interstitial only — see `docs/ADS-MONETIZATION.md` §A.3). So **every**
 * number, string and sprite below is ❓ PLACEHOLDER / product decision. None of
 * them may be given an RVA.
 *
 * ## Whose geometry it borrows
 *
 * `SettingsPresenter` — the closest shipped panel in shape (a titled yellow
 * frame with an inner panel, a red close disc hanging off its top-right corner
 * and one button at the bottom), and the only one the player opens on purpose
 * rather than being shown at the end of a level. Concretely, reused verbatim:
 *
 *     frame           Panel_Yellow(Soft), 9-slice 125 px @ 130 -> 96.154 u
 *     Contents        494 wide, inset 30 top and bottom for the inner panel
 *     inner panel     Panel_Inside_Yellow(Soft), 124.92 px @ 130 -> 96.095 u
 *     Title           365.5 x 90, pivot bottom, 10 above Contents  Panel_Title_Blue
 *     CloseButtonBG   75 square, centre (-17, +21) from Contents' top-right
 *                       btn_2_red + Exit, same as `#settingsPopup .popupClose`
 *     buy button      .popupButtonBase (btn_1_base2) + .popupButton, i.e. the
 *                       same gold rim + `--buttonInset` pairing as Try Again,
 *                       Continue and Privacy — recoloured `btn_1_green`
 *
 * ❓ What is *not* borrowed, i.e. invented here: the frame's height (a settings
 * panel is 427.7 tall — 557.4 in the prefab, trimmed when `Privacy` went — and
 * holds two switch rows plus a divider; this one holds one paragraph and two
 * buttons), the description and restore rows' rects, and every string.
 *
 * ## Behaviour — `docs/ADS-MONETIZATION.md` §A.2.4
 *
 * DiceFlow's store panel, which is the reference the doc reads:
 *
 *  - the price is fetched from the store **once** and falls back to `$4.99`
 *    until (and if) it answers;
 *  - both the buy and the restore button carry a double-tap guard, and while
 *    the store is being talked to the panel is locked with no way to cancel —
 *    only the store's answer unlocks it;
 *  - "already bought" is pushed from three sources (purchase, restore, and the
 *    app's unsolicited `remove_ads_changed`), so the owned state is driven by
 *    `IapService.onAdsRemovedChanged`, never by the buy call's return alone.
 *
 * Analytics: this panel logs exactly one event, `remove_ads_open`.
 * `remove_ads_start` / `_result` / `_restore` belong to `IapService`, which is
 * the side that knows what the store said.
 */
import type { IapService } from '../monetization/iap'
import { monetizationAnalytics } from '../monetization/analytics'
import { Popup, el, sprite } from './popups'

/** ❓ PLACEHOLDER / product decision — no shipped panel to read these off. */
export const NO_ADS_TEXT = {
  title: 'REMOVE ADS',
  /** One line, deliberately narrow enough to wrap into the inner panel. */
  desc: 'One purchase, no more interstitials between levels. Forever.',
  /** ❓ Fallback price until the store answers — §A.2.4 pins 4.99 $. */
  fallbackPrice: '$4.99',
  restore: 'Restore Purchases',
  /** Shown instead of both buttons once the product is owned. */
  owned: '✓ Ads removed',
  /**
   * What the store's answer says, in the panel itself.
   *
   * DiceFlow puts these in a toast (`K1`); this port has no toast layer, and a
   * store round-trip that says nothing at all reads as a dead button — which is
   * exactly what a plain browser produces, since there is no `AndroidBridge` to
   * answer and every call falls back to "no".
   */
  status: {
    restoreNone: 'No purchases to restore',
    purchasePending: 'Purchase pending…',
    purchaseFailed: 'Purchase failed',
    purchaseCancelled: '',
  },
}

export interface NoAdsHandlers {
  /** The X, and the scrim behind it — the caller lifts whatever pause it set. */
  onClose(): void
}

export class NoAdsPopup extends Popup {
  private readonly iap: IapService
  private readonly buyButton: HTMLButtonElement
  private readonly priceText: HTMLElement
  private readonly restoreButton: HTMLButtonElement
  /** The store's last word, cleared whenever the panel reopens or a call starts. */
  private readonly statusText: HTMLElement
  /** DiceFlow's `q4` / `v4` — one guard for both buttons, since one overlay
   *  covers the whole panel while the store is answering. */
  private busy = false
  /** §A.2.4: the store is asked for the price once per session, not per open. */
  private priceAsked = false
  /**
   * The level the panel was opened on, kept so the two store calls log the same
   * level the open did. ❓ 0 is "no level" — the panel is openable from a menu
   * as well as from the HUD.
   */
  private level = 0

  constructor(parent: HTMLElement, iap: IapService, handlers: NoAdsHandlers) {
    super(parent, 'noAdsPopup')
    this.iap = iap

    const close = el('button', 'popupClose', sprite('popupCloseIcon', 'Exit.png')) as HTMLButtonElement
    close.type = 'button'
    close.setAttribute('aria-label', 'close')
    close.addEventListener('click', () => {
      // A store round-trip has no cancel: the panel stays up until the store
      // answers, exactly as in the reference build.
      if (this.busy) return
      this.close()
      handlers.onClose()
    })

    this.priceText = el('span', 'popupButtonText', NO_ADS_TEXT.fallbackPrice)
    this.buyButton = el('button', 'popupButton noAdsBuyButton', this.priceText) as HTMLButtonElement
    this.buyButton.type = 'button'
    this.buyButton.addEventListener('click', () => void this.buy())

    this.restoreButton = el('button', 'noAdsRestore', el('span', 'noAdsRestoreText', NO_ADS_TEXT.restore)) as HTMLButtonElement
    this.restoreButton.type = 'button'
    this.restoreButton.addEventListener('click', () => void this.restore())

    this.statusText = el('div', 'noAdsStatus')

    this.frame.append(
      el(
        'div',
        'noAdsContents',
        el('div', 'noAdsInner'),
        el('div', 'noAdsTitle', el('span', 'noAdsTitleText', NO_ADS_TEXT.title)),
        close,
        el('p', 'noAdsDesc', NO_ADS_TEXT.desc),
        el('div', 'popupButtonBase noAdsBuy', this.buyButton),
        this.restoreButton,
        this.statusText,
        el('div', 'noAdsOwned', NO_ADS_TEXT.owned),
      ),
    )

    this.setOwned(iap.adsRemoved)
    // Purchase, restore and the app's `remove_ads_changed` push all land here.
    iap.onAdsRemovedChanged((removed) => this.setOwned(removed))
  }

  /**
   * `remove_ads_open`, then the price, then the panel.
   *
   * The price is awaited *beside* the open, not before it: a store that never
   * answers must not keep the panel shut, and the fallback is already on the
   * button.
   */
  override open(level = 0): void {
    this.level = level
    monetizationAnalytics.log('remove_ads_open', level)
    this.setBusy(false)
    this.setStatus('')
    this.setOwned(this.iap.adsRemoved)
    if (!this.priceAsked) {
      this.priceAsked = true
      this.iap.price().then(
        (price) => {
          if (price) this.priceText.textContent = price
        },
        // A store that fails to quote leaves the fallback in place; the panel
        // is not an error surface.
        () => {},
      )
    }
    super.open()
  }

  /** `IapService.buyRemoveAds` — one flight at a time, panel locked meanwhile. */
  private async buy(): Promise<void> {
    if (this.busy || this.iap.adsRemoved) return
    this.setBusy(true)
    this.setStatus('')
    try {
      const result = await this.iap.buyRemoveAds(this.level)
      // 'success' also arrives through `onAdsRemovedChanged`; setting it here
      // as well costs nothing and covers a bridge that only answers the call.
      if (result === 'success') this.setOwned(true)
      else if (result === 'pending') this.setStatus(NO_ADS_TEXT.status.purchasePending)
      else if (result === 'failed') this.setStatus(NO_ADS_TEXT.status.purchaseFailed)
      else this.setStatus(NO_ADS_TEXT.status.purchaseCancelled)
    } finally {
      // 'pending' (a deferred / parental-approval purchase) unlocks too: the
      // grant will arrive later as a `remove_ads_changed` push.
      this.setBusy(false)
    }
  }

  /** `IapService.restore` — mandatory for a non-consumable on Google Play. */
  private async restore(): Promise<void> {
    if (this.busy || this.iap.adsRemoved) return
    this.setBusy(true)
    this.setStatus('')
    try {
      if (await this.iap.restore(this.level)) this.setOwned(true)
      else this.setStatus(NO_ADS_TEXT.status.restoreNone)
    } finally {
      this.setBusy(false)
    }
  }

  /** Both buttons off, the whole panel dimmed; only a store answer clears it. */
  private setBusy(busy: boolean): void {
    this.busy = busy
    this.root.classList.toggle('busy', busy)
    this.buyButton.disabled = busy || this.iap.adsRemoved
    this.restoreButton.disabled = busy || this.iap.adsRemoved
  }

  /**
   * The one piece of feedback the panel has: floating text that pops, rides up
   * and is gone — not a label that sits there. A button whose store call said
   * "no" must not look like a button that did nothing.
   *
   * Retriggering needs the class off, a **layout read**, then the class on.
   * Without the read the browser coalesces both writes into one frame, sees no
   * change and never restarts the animation — so a second Restore tap would be
   * silent, which is the exact bug this method exists to fix.
   */
  private setStatus(message: string): void {
    this.statusText.classList.remove('on')
    if (message === '') return
    this.statusText.textContent = message
    void this.statusText.offsetWidth
    this.statusText.classList.add('on')
  }

  /** Owned: the two buttons give way to the confirmation line. */
  private setOwned(owned: boolean): void {
    this.root.classList.toggle('owned', owned)
    this.buyButton.disabled = owned || this.busy
    this.restoreButton.disabled = owned || this.busy
  }
}
